import { formatDistanceToNowStrict } from 'date-fns';
import { LinearGradient } from 'expo-linear-gradient';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    H6Regular,
    HolisticAccountLogo,
    InfoButton,
    LabelEmphasized,
    Private,
    redactPrivate,
    Spinner,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import AccountStateIcon from '../../banking/components/icons/AccountStateIcon';
import { FRUITFUL_INSTITUTION_NAME } from '../../banking/types/BankAccount';
import {
    centsToDollars,
    formatDollars,
    formatMaskedValue,
} from '../../common/utils/bankingUtils';
import {
    isAccountInErrorState,
    isLiabilityAccountType,
} from '../../common/utils/holisticUtils';
import {
    AccountCategory,
    type AccountGroupCategory,
    type AccountSummary,
    FruitfulAccountCategory,
    Provider as AccountProvider,
    Provider,
    SpendAccountCategory,
} from '../types/HolisticSummary';
import AddInstitutionLinkButton from './AddInstitutionLinkButton';

export const AccountRowGroupHeader = ({
    accountGroupCategory,
    heading,
    openDisclosureSheet,
    openInstitutionSheet,
    setInstitutionSheetSpendAccountCategory,
}: {
    accountGroupCategory: AccountGroupCategory;
    heading: string;
    openDisclosureSheet: () => void;
    openInstitutionSheet: () => void;
    setInstitutionSheetSpendAccountCategory: (
        category: SpendAccountCategory | undefined,
    ) => void;
}) => {
    const isFruitfulGroup = Object.values(FruitfulAccountCategory).includes(
        accountGroupCategory as FruitfulAccountCategory,
    );

    const spendAccountCategory = Object.values(SpendAccountCategory).includes(
        accountGroupCategory as SpendAccountCategory,
    )
        ? (accountGroupCategory as SpendAccountCategory)
        : undefined;

    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            marginBottom={4}
            $sm={{ marginBottom: 8 }}
        >
            {isFruitfulGroup ? (
                <XStack
                    alignItems="center"
                    marginBottom={
                        -6 // offsets padding of InfoButton
                    }
                >
                    <H6Regular>{heading}</H6Regular>
                    <InfoButton subtle onPress={openDisclosureSheet} />
                </XStack>
            ) : (
                <>
                    <H6Regular>{heading}</H6Regular>
                    <AddInstitutionLinkButton
                        onPress={() => {
                            setInstitutionSheetSpendAccountCategory(
                                spendAccountCategory,
                            );
                            openInstitutionSheet();
                        }}
                    />
                </>
            )}
        </XStack>
    );
};

interface AccountProps {
    account: AccountSummary;
    onPress: () => void;
}

export const AccountRow = ({ account, onPress }: AccountProps) => {
    const { name, accountNumberMasked, institutionName, logo, provider } =
        account;
    const institutionNameDisplay = `${institutionName === FRUITFUL_INSTITUTION_NAME ? '' : institutionName} ${formatMaskedValue(accountNumberMasked)}`;

    return (
        <XStack
            gap={8}
            paddingVertical={12}
            $sm={{ paddingHorizontal: 12, borderRadius: 12 }}
            alignItems="center"
            justifyContent="flex-start"
            onPress={onPress}
            cursor="pointer"
            hoverStyle={{ backgroundColor: '$gray100' }}
            flex={1}
        >
            <HolisticAccountLogo
                institutionName={institutionName}
                logo={logo}
                provider={provider}
            />
            <YStack flex={1} gap={0} justifyContent="center">
                <XStack
                    flex={1}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    paddingTop={2}
                    gap={16}
                >
                    <BodyMediumRegular
                        color="$gray1200"
                        numberOfLines={1}
                        paddingTop={1}
                        textOverflow="ellipsis"
                        ellipsizeMode="middle"
                        minWidth="40%"
                        flexShrink={1}
                        {...redactPrivate()}
                    >
                        {name}
                    </BodyMediumRegular>
                    <AccountBalance account={account} />
                </XStack>
                <XStack
                    flex={1}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    paddingBottom={4}
                    gap={16}
                >
                    <LabelEmphasized
                        color="$gray1000"
                        numberOfLines={1}
                        textOverflow="ellipsis"
                        ellipsizeMode="middle"
                        minWidth="40%"
                        flexShrink={1}
                        {...redactPrivate()}
                    >
                        {institutionNameDisplay}
                    </LabelEmphasized>
                    <AccountLastUpdated account={account} />
                </XStack>
            </YStack>
        </XStack>
    );
};

const AccountBalance = ({ account }: { account: AccountSummary }) => {
    const { getColor } = useThemeColors();
    const { category, currentBalanceCents, type } = account;
    const isErrorState = isAccountInErrorState(account);

    // liability accounts should always reverse the balance, except for a balance of 0 which should be left alone
    const normalizedCurrentBalanceCents =
        category === AccountCategory.SPEND &&
        isLiabilityAccountType(type) &&
        currentBalanceCents != null &&
        currentBalanceCents !== 0
            ? currentBalanceCents * -1
            : currentBalanceCents;

    const noBalanceElement = isErrorState ? (
        <View width={70}>
            <LinearGradient
                colors={[getColor('$gray100'), getColor('$white')]}
                locations={[0, 0.9]}
                start={{ x: 0, y: 1 }}
                end={{ x: 1, y: 1 }}
                style={{
                    borderRadius: 6,
                    height: '100%',
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                }}
            />
        </View>
    ) : (
        <Spinner flex={0} width="auto" />
    );

    const accountBalanceElement =
        normalizedCurrentBalanceCents == null ? (
            noBalanceElement
        ) : (
            <Private>
                <BodyLargeRegular flexGrow={1} textAlign="right">
                    {formatDollars(
                        centsToDollars(normalizedCurrentBalanceCents),
                    )}
                </BodyLargeRegular>
            </Private>
        );

    if (isErrorState) {
        return (
            <XStack gap={4}>
                <AccountStateIcon state="error" />
                {accountBalanceElement}
            </XStack>
        );
    }

    return accountBalanceElement;
};

const AccountLastUpdated = ({ account }: { account: AccountSummary }) => {
    const { currentBalanceCents, institutionName, lastUpdated, provider } =
        account;
    const isFruitfulDepositAccount =
        institutionName === FRUITFUL_INSTITUTION_NAME &&
        provider === Provider.SYNCTERA;

    if (currentBalanceCents == null || isFruitfulDepositAccount) {
        return null;
    }

    const text =
        account.provider === AccountProvider.MANUAL
            ? 'Manually tracked'
            : formatDistanceToNowStrict(
                  lastUpdated != null ? new Date(lastUpdated) : new Date(),
                  { addSuffix: true },
              );

    return (
        <LabelEmphasized
            flexGrow={1}
            textAlign="right"
            color="$gray1000"
            textWrap="nowrap"
        >
            {text}
        </LabelEmphasized>
    );
};
